import { useEffect, useRef, useState } from 'react';
import { useUvsTimedOut } from '@shoflo/polymer';
import { useDispatch, useSelector } from 'react-redux';

import {
  streamStarting,
  streamingActive
} from 'hooks/useStreamButtonState/utils';
import StreamAlerts from './StreamAlerts';

const emptyArray = [];

/**
 * @description Host useStreamAlerts Custom Hook
 * Manages Notifications that show up through the lifetime of a stream.
 * Whenever this custom hook runs, it gets StreamAlerts that meet the conditions to show,
 * these alerts are queued and then are fired as Notifications using setTimeout
 * Logic for conditions: https://wiki.cvent.com/display/STUDIO/Studio+Alerts%2C+Stream+Button%2C+Countdown+Timer+States
 */
function useStreamAlerts() {
  const serverTimeDelta = useSelector(state => state.serverTimeDelta);
  const broadcast = useSelector(state => state.broadcast);
  const streamEndpoints = useSelector(
    state => state.streamEndpoints || emptyArray
  );
  const dispatch = useDispatch();

  const previousStreamEndpoints = useRef([]);
  const [currentAlerts, setCurrentAlerts] = useState([]);

  const timedOut = useUvsTimedOut({
    uvsEntities: streamEndpoints,
    timeoutMs: 60000
  });

  useEffect(() => {
    // Only run if there are endpoints
    if (streamEndpoints.length > 0) {
      // this variable holds the value for when the stream starts, it is short-lived
      let flashStarted = false;

      // this block of code captures the short-lived flashStarted value
      // if endpoint is starting and real time stream status is started
      // then flashStarted is set to true
      if (
        streamStarting(previousStreamEndpoints.current) &&
        streamingActive(streamEndpoints)
      ) {
        flashStarted = true;
      }

      try {
        const streamAlerts = new StreamAlerts(
          broadcast,
          streamEndpoints,
          serverTimeDelta,
          flashStarted,
          timedOut
        );

        // get all StreamAlerts
        setCurrentAlerts(streamAlerts.alerts);
        previousStreamEndpoints.current = streamEndpoints;
      } catch (e) {
        console.warn('Stream alert error: ', e);
      }
    }
  }, [
    dispatch,
    timedOut,
    broadcast,
    serverTimeDelta,
    streamEndpoints,
    previousStreamEndpoints
  ]);

  return currentAlerts;
}

export default useStreamAlerts;
